import { Box, Icon, Tag, Text, Tooltip, useToggle } from '@factorialco/deprecated-design-system'
import {
  ContractVersionPageContractVersionFragment,
  ContractVersionsPageEmployeeFragment,
} from 'generated/resources/graphql'

import { observer } from 'mobx-react'

import i18n from 'modules/core/lib/i18n'

import {
  MandatoryFieldName,
  isMandatoryFieldName,
} from 'modules/bookkeepers/store/models/IncidenceRule'
import { useContractCues } from 'modules/contracts/lib/contractCuesContext'
import { getCountryField } from 'modules/contracts/lib/getCountryField'

import TooltipContent from './TooltipContent'

const mapContractVersionField = (
  contractVersion: ContractVersionPageContractVersionFragment,
  isVersionDataEnabled: boolean,
  fieldName: MandatoryFieldName
) => {
  switch (fieldName) {
    case 'starts_on':
      return contractVersion.startsOn
    case 'ends_on':
      return contractVersion.endsOn
    case 'salary_amount':
      return contractVersion.salaryAmount
    case 'es_contract_type':
      return (
        getCountryField({
          isVersionDataEnabled,
          contractVersion,
          field: 'contract_type',
        })?.name ?? null
      )
    case 'es_professional_category_name':
      return contractVersion.spainProfessionalCategory?.name
    case 'fr_contract_type_name':
      return (
        getCountryField({
          isVersionDataEnabled,
          contractVersion,
          field: 'contract_type',
        })?.name ?? null
      )
    case 'fr_coefficient':
      return contractVersion.frCoefficient
    case 'fr_mutual_name':
      return contractVersion.franceMutual?.name
    case 'fr_work_type_name':
      return contractVersion.franceWorkingDayType?.name
    case 'working_hours':
      return contractVersion.workingHours
    case 'working_hours_frequency':
      return contractVersion.workingHoursFrequency
    case 'working_week_days':
      return contractVersion.workingWeekDays
  }
}

const mapEmployeeField = (
  employee: ContractVersionsPageEmployeeFragment,
  fieldName: MandatoryFieldName
) => {
  switch (fieldName) {
    case 'first_name':
      return employee.firstName
    case 'last_name':
      return employee.lastName
    case 'social_security_number':
      return employee.socialSecurityNumber
    case 'identifier':
      return employee.identifier
    case 'identifier_type':
      return employee.identifierType
    case 'address':
      return employee.addressLine1
    case 'gender':
      return employee.gender
    case 'birthday_on':
      return employee.birthdayOn
    case 'bank_number':
      return employee.bankNumber
    case 'company_identifier':
      return employee.companyIdentifier
    case 'seniority_calculation_date':
      return employee.seniorityCalculationDate
  }
}

// Helper function to map incidence rule field names from contractCues Contract collection to graphql contract version
const getContractVersionFieldValue = (
  contractVersion: ContractVersionPageContractVersionFragment,
  employee: ContractVersionsPageEmployeeFragment,
  isVersionDataEnabled: boolean,
  fieldName: MandatoryFieldName
): unknown => {
  const mappedField = mapContractVersionField(contractVersion, isVersionDataEnabled, fieldName)
  if (mappedField) {
    return mappedField
  }

  const employeeMappedField = mapEmployeeField(employee, fieldName)
  if (employeeMappedField) {
    return employeeMappedField
  }

  return undefined
}
interface Props {
  selectedContractVersion: ContractVersionPageContractVersionFragment
  employee: ContractVersionsPageEmployeeFragment
}

const MissingData = ({ selectedContractVersion, employee }: Props) => {
  const [showTooltip, toggleTooltip] = useToggle(false)
  const contractCues = useContractCues()
  const isVersionDataEnabled = contractCues.isVersionDataEnabled ?? false

  if (!contractCues.hasCues) return null

  const pendingRules = contractCues.incidenceRules.filter((rule) => {
    const fieldName = rule.field
    if (isMandatoryFieldName(fieldName)) {
      const fieldValue = getContractVersionFieldValue(
        selectedContractVersion,
        employee,
        isVersionDataEnabled,
        fieldName
      )
      return !fieldValue || fieldValue === ''
    }
    return false
  })

  if (pendingRules.length === 0) return null

  return (
    <Tooltip.ItemList attachment="bottom center" open={showTooltip} toggle={toggleTooltip}>
      <Tag type="radical" inline>
        <Box gap="s4" flexDirection="row" alignItems="center">
          <Icon.Medium icon="alert-circle-line" color="radicalRed1200" />
          <Text.Normal capitalize weight="medium" size="200" color="grey1000">
            {i18n.t('contract_versions.header.tags.missing_data.title')}
          </Text.Normal>
        </Box>
      </Tag>
      <TooltipContent
        pendingRules={pendingRules}
        employeeId={employee.id.toString()}
        contractVersionId={selectedContractVersion.id?.toString() ?? ''}
      />
    </Tooltip.ItemList>
  )
}

export default observer(MissingData)
